import { isString } from '../utils';
import { formatError } from '../utils/VTO/errorFormatter';
import { getVtoData } from '../utils/ProductGetters';
import {
  isValidMode,
  mixArrElements,
  generateLookObject,
  generateFeaturesCollection,
  cloneCanvas,
} from '../utils/VTO';

import { getPdpProductById } from '../services/apiClient';
import {
  liveLook,
  photoLook,
  generatePhotoCanvas,
  generateLiveCanvas,
  destroyLiveLook,
  destroyPhotoLook,
  setCoordinates,
} from '../services/ModifaceSDK';

import {
  SECOND_MODES,
  MAIN_MODES,
  ADJUSTABLE_LABELS_BY_CATEGORY,
  MULTI_LOOKS_MAX_COUNT,
} from '../config/VTO';

const getDefaultState = () => ({
  productId: null,
  product: null,
  adjustableCoords: {},
  previousAdjustableCoords: {},
  multiLooksCollection: [],
  mainMode: '',
  secondMode: SECOND_MODES.UPLOAD,
  beforeRenderedCanvas: null,
  afterRenderedCanvas: null,
  error: null,
  isLoading: false,
  unsupportedColor: false,
});

export default {
  namespaced: true,

  state() {
    return getDefaultState();
  },

  getters: {
    product: (state) => state.product,
    lookObject: (state) => generateLookObject(state.product),
    secondMode: (state) => state.secondMode,
    canvases: (state) => ({
      beforeRenderedCanvas: state.beforeRenderedCanvas,
      afterRenderedCanvas: state.afterRenderedCanvas,
    }),
    featuresCollection: (state, getters) => generateFeaturesCollection(getters.product, getters.lookObject),
    error: (state) => formatError(state.error),
    unsupportedColor: (state) => state.unsupportedColor,
    adjustableLabel: (state, getters) => ADJUSTABLE_LABELS_BY_CATEGORY[getters.product.category],
    multiLooksCollection: (state) => state.multiLooksCollection,
    selectedColorName: (state) => state.product.colorMap[state.product.selectedColorId].name,

    isErrorVisible: (state) => !!state.error,
    isLoadingVisible: (state) => state.isLoading,
    isPhotoMode: (state) => state.mainMode === MAIN_MODES.PHOTO,
    isLiveMode: (state) => state.mainMode === MAIN_MODES.LIVE,
    isUploadMode: (state) => state.secondMode === SECOND_MODES.UPLOAD,
    isSelectMode: (state) => state.secondMode === SECOND_MODES.SELECT,
    isBeforeAfterMode: (state) => state.secondMode === SECOND_MODES.BEFORE_AFTER,
    isCompareMode: (state) => state.secondMode === SECOND_MODES.COMPARE,
    isAdjustingMode: (state) => state.secondMode === SECOND_MODES.ADJUSTING,
    isMouthOpen: (state) => (state.adjustableCoords.facePoints
      ? state.adjustableCoords.facePoints.mouthopen
      : false),
  },

  mutations: {
    setMainMode(state, mode) {
      if (!isValidMode(mode, MAIN_MODES)) {
        throw new Error('VTO module error: mode parameter is invalid');
      }

      state.mainMode = mode;
    },

    setSecondMode(state, mode) {
      if (!isValidMode(mode, SECOND_MODES)) {
        throw new Error('VTO module error: mode parameter is invalid');
      }

      state.secondMode = mode;
    },

    setMultiLook(state, look) {
      const isLookAdded = state.multiLooksCollection.find((element) => element.name === look.name);

      if (isLookAdded || state.unsupportedColor) {
        return;
      }

      if (state.multiLooksCollection.length === MULTI_LOOKS_MAX_COUNT) {
        state.multiLooksCollection.pop();
      }

      state.multiLooksCollection.unshift(look);
    },

    clearMultiLooksCollection(state) {
      state.multiLooksCollection = [];
    },

    toggleLoading(state) {
      state.isLoading = !state.isLoading;
    },

    setProductId(state, id) {
      state.productId = id;
    },

    setProduct(state, product) {
      state.product = getVtoData(product);
    },

    setSelectedColor(state, id) {
      state.product.selectedColorId = id;
    },

    setUnsupportedColor(state, boolean) {
      state.unsupportedColor = boolean;
    },

    setError(state, error) {
      if (error === null) {
        state.error = null;
        return;
      }

      if (!error || !isString(error.message)) {
        throw new Error('VTO module error: error parameter should be instance of Error class');
      }

      state.error = error;
      console.error(error); // eslint-disable-line no-console
    },

    setCanvases(state, { beforeRenderedCanvas, afterRenderedCanvas }) {
      if (beforeRenderedCanvas) {
        state.beforeRenderedCanvas = beforeRenderedCanvas;
      }
      if (afterRenderedCanvas) {
        state.afterRenderedCanvas = afterRenderedCanvas;
      }
    },

    setCoordinate(state, { feature, index, coords } = {}) {
      state.adjustableCoords.facePoints[feature][index] = coords;
    },

    setAdjustableCoordinates(state, adjustableCoords) {
      if (adjustableCoords.facePoints.leftFullBrow2D) {
        // one of the elements of the array is on a wrong place, which breaks the display of 'adjusting mode'
        mixArrElements(adjustableCoords.facePoints.leftFullBrow2D, 4, 6);
      }

      state.adjustableCoords = adjustableCoords;
    },

    cloneAdjustableCoordinates(state) {
      const coordimatesString = JSON.stringify(state.adjustableCoords);
      state.previousAdjustableCoords = JSON.parse(coordimatesString);
    },

    resetAdjustableCoordinates(state) {
      const previousAdjustableCoordsStr = JSON.stringify(state.previousAdjustableCoords);
      state.adjustableCoords = JSON.parse(previousAdjustableCoordsStr);
    },

    resetPreviousAdjustableCoordinates(state) {
      state.previousAdjustableCoords = getDefaultState().previousAdjustableCoords;
    },

    toggleMouthState(state, value) {
      state.adjustableCoords.facePoints.mouthopen = value;
    },

    resetState(state) {
      Object.assign(state, getDefaultState());
    },
  },

  actions: {
    async getProductById({ state, commit }) {
      try {
        const { product } = await getPdpProductById({ id: state.productId, size: 'medium' });

        commit('setProduct', product[0]);
      } catch (err) {
        commit('setError', err);
      }
    },

    async generateLiveLook({
      rootState, getters, commit, dispatch,
    }) {
      // clear error if we're trying to add file for the second time
      commit('setError', null);
      commit('toggleLoading');

      await dispatch('getProductById');

      try {
        const { beforeRenderedCanvas, afterRenderedCanvas } = await liveLook({
          lookObject: getters.lookObject,
          hostlink: rootState.envProps.hostlink,
        });

        commit('setMainMode', MAIN_MODES.LIVE);
        commit('setSecondMode', SECOND_MODES.SELECT);
        commit('setCanvases', { beforeRenderedCanvas, afterRenderedCanvas });
      } catch (err) {
        commit('setError', err);
      }

      commit('toggleLoading');
    },

    async generatePhotoLook({
      rootState, getters, commit, dispatch,
    }, { file } = {}) {
      // clear error if we're trying to add file for the second time
      commit('setError', null);
      commit('toggleLoading');

      await dispatch('getProductById');

      try {
        const { beforeRenderedCanvas, afterRenderedCanvas, adjustableCoords } = await photoLook({
          file,
          lookObject: getters.lookObject,
          hostlink: rootState.envProps.hostlink,
        });

        commit('setMainMode', MAIN_MODES.PHOTO);
        commit('setSecondMode', SECOND_MODES.SELECT);
        commit('setCanvases', { beforeRenderedCanvas, afterRenderedCanvas });
        commit('setAdjustableCoordinates', adjustableCoords);
      } catch (err) {
        commit('setError', err);
      }

      commit('toggleLoading');
    },

    async destroyVTO({ getters, commit }) {
      if (getters.isPhotoMode) {
        await destroyPhotoLook();
      } else if (getters.isLiveMode) {
        await destroyLiveLook();
      }

      commit('resetState');
    },

    async changeColor({ getters, commit }) {
      let result;

      try {
        if (getters.isPhotoMode) {
          result = await generatePhotoCanvas({ lookObject: getters.lookObject });
        } else if (getters.isLiveMode) {
          result = await generateLiveCanvas({ lookObject: getters.lookObject });
        }
        commit('setUnsupportedColor', false);
        commit('setCanvases', { afterRenderedCanvas: result.renderedCanvas });
      } catch (err) {
        commit('setUnsupportedColor', true);
        console.error(err); // eslint-disable-line no-console
      }
    },

    setCoordinates({ state, commit }) {
      setCoordinates(state.adjustableCoords);
      commit('resetPreviousAdjustableCoordinates');
    },

    setMultiLook({ getters, commit }) {
      const canvas = cloneCanvas(getters.canvases.afterRenderedCanvas);
      commit('setMultiLook', {
        canvas,
        name: getters.selectedColorName,
      });
    },

    clearError({ commit }) {
      commit('setError', null);
    },
  },
};
